import jsQR from "jsqr";
import QRCode from "qrcode";

export async function qrDataUrl(value: string): Promise<string | undefined> {
  if (!value || value.length > 10_000) return undefined;
  try {
    return await QRCode.toDataURL(value, { errorCorrectionLevel: "L", margin: 2, width: 420 });
  } catch { return undefined; }
}

export async function scanQrWithCamera(): Promise<string> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera scanning is not available in this browser.");
  const dialog = document.createElement("dialog");
  dialog.className = "qr-camera-dialog";
  dialog.innerHTML = '<div class="dialog-heading"><div><p class="eyebrow">QR scanner</p><h2>Point at the collaboration code</h2></div><button class="icon-button" data-close-camera aria-label="Close camera">×</button></div><video autoplay muted playsinline aria-label="Camera preview"></video><p aria-live="polite">Looking for a LineByLine QR code…</p>';
  document.body.append(dialog);
  const video = dialog.querySelector("video")!;
  let stream: MediaStream;
  try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false }); }
  catch (error) { dialog.remove(); throw new Error(error instanceof Error ? `Could not open the camera: ${error.message}` : "Could not open the camera."); }
  video.srcObject = stream;
  await video.play();
  dialog.showModal();
  return new Promise<string>((resolve, reject) => {
    let frame = 0;
    let finished = false;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    const cleanup = () => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(frame);
      stream.getTracks().forEach((track) => track.stop());
      dialog.close();
      dialog.remove();
    };
    const cancel = () => { cleanup(); reject(new Error("Camera scanning was cancelled.")); };
    dialog.querySelector("[data-close-camera]")?.addEventListener("click", cancel, { once: true });
    dialog.addEventListener("cancel", (event) => { event.preventDefault(); cancel(); }, { once: true });
    let lastScan = 0;
    const scan = (time: number) => {
      if (finished) return;
      if (context && video.videoWidth && time - lastScan > 100) {
        lastScan = time;
        const scale = Math.min(1, 1200 / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQR(image.data, image.width, image.height, { inversionAttempts: "attemptBoth" });
        if (result?.data) { const value = result.data; cleanup(); resolve(value); return; }
      }
      frame = requestAnimationFrame(scan);
    };
    frame = requestAnimationFrame(scan);
  });
}

export async function readQrImage(file: File): Promise<string> {
  if (file.size > 12_000_000) throw new Error("The QR image is too large.");
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("This browser cannot read QR images.");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsQR(image.data, image.width, image.height, { inversionAttempts: "attemptBoth" });
  if (!result?.data) throw new Error("No collaboration QR code was found in that image.");
  return result.data;
}
